import { useState, useEffect, useMemo } from "react";
import { Popover, Modal, Tabs, Empty, message } from "antd";
import {
  MoneyCollectOutlined,
  ArrowRightOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SendOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { motion, AnimatePresence } from "framer-motion";

/* ============================================================================
 * CHANGES IN THIS VERSION:
 *  Birthdays & Anniversaries now come from the retail ERP API, table
 *  CUSTOMER (see BIRTHDAY / ANNIVERSARY DATA LAYER below). Dues, UI, dialogs
 *  and the send-greeting logic are exactly as before.
 * ==========================================================================*/

// Base URL of the retail ERP API (from the URLs you shared).
const API_BASE = "https://retailtextiles.timeserasoftware.in";

/* ════════════════════════════════════════════════════════════════
   DATA LAYER — Today's Dues  (no UI here)
   ════════════════════════════════════════════════════════════════ */

/**
 * TRANS_ENTRY_DATA_CUST — customer ledger entries.
 * ⚠ CONFIRM — credit-side layout is assumed: due = JAMA − NAMA per customer.
 * If a credit invoice shows its amount in NAMA instead, swap the two keys.
 */
const DUES_SOURCE = {
  table: "TRANS_ENTRY_DATA_CUST",
  dateCol: "ENTRYDATE",
  partyKey: "PARTYNAME",
  areaKey: "AREA",
  billedKey: "JAMA",
  settledKey: "NAMA",
  particularsKey: "PARTICULARS",
  particularsMatch: "SALES INVOICE",
  typeKey: "TRANS_TYPE",
  cashMarker: "CASH",
  cancelKey: "CHQCANCELL",
};

const groupPartyDues = (rows, src = DUES_SOURCE) => {
  const grouped = new Map();

  rows.forEach((item) => {
    if (String(item[src.cancelKey] ?? "").toUpperCase() === "YES") return;
    if (
      !String(item[src.particularsKey] ?? "")
        .toUpperCase()
        .includes(src.particularsMatch)
    )
      return;
    if (
      String(item[src.typeKey] ?? "")
        .toUpperCase()
        .includes(src.cashMarker)
    )
      return;

    const party = item[src.partyKey];
    if (!party || party === "-") return;

    const area = item[src.areaKey];
    const key = `${party}|${area || ""}`;

    if (!grouped.has(key)) {
      grouped.set(key, { CustName: party, Area: area, billed: 0, settled: 0 });
    }
    const entry = grouped.get(key);
    entry.billed += Number(item[src.billedKey] || 0);
    entry.settled += Number(item[src.settledKey] || 0);
  });

  return [...grouped.values()]
    .map((e) => ({
      CustName: e.CustName,
      Area: e.Area,
      BalanceAmt: e.billed - e.settled,
    }))
    .filter((e) => e.BalanceAmt > 0)
    .sort((a, b) => b.BalanceAmt - a.BalanceAmt);
};

const useTodayDues = (tenantName) => {
  const [list, setList] = useState([]);

  useEffect(() => {
    const src = DUES_SOURCE;
    const fetchDues = async () => {
      try {
        const today = dayjs();
        const start = today.format("YYYY-MM-DD");
        const end = today.add(1, "day").format("YYYY-MM-DD");
        const where = `${src.dateCol}>='${start}' AND ${src.dateCol}<'${end}'`;

        const res = await axios.get(
          `${API_BASE}/api/ERP/GetDataFromGivenTableNameWithWhere`,
          {
            params: { tableName: src.table, where },
            headers: tenantName ? { tenantName } : {},
          },
        );
        const rows = Array.isArray(res.data) ? res.data : [];

        if (rows.length) {
          const missing = [
            src.partyKey,
            src.billedKey,
            src.settledKey,
            src.particularsKey,
          ].filter((k) => !Object.prototype.hasOwnProperty.call(rows[0], k));
          if (missing.length) {
            console.warn(
              `Today's Dues → ${src.table}: missing column(s) ${missing.join(", ")}. ` +
              `Columns returned: ${Object.keys(rows[0]).join(", ")}`,
            );
          }
        }

        setList(groupPartyDues(rows, src));
      } catch (e) {
        console.error("Error fetching dues:", e);
        setList([]);
      }
    };

    fetchDues();
  }, [tenantName]);

  const total = useMemo(
    () => list.reduce((a, d) => a + (d.BalanceAmt || 0), 0),
    [list],
  );

  return { list, total };
};

/* ════════════════════════════════════════════════════════════════
   DATA LAYER — Birthdays & Anniversaries  (no UI here)
   ----------------------------------------------------------------
   Table : CUSTOMER   (GetDataFromGivenTableNameWithWhere)

   Your sample URLs used  DOB='10/01/2026'  — that is an EXACT-date match,
   so it would only return customers born on that very date in 2026.
   A birthday / anniversary repeats every year, so we match on MONTH + DAY
   only:  MONTH(DOB)=10 AND DAY(DOB)=1   (SQL Server syntax).

   One call per kind (birthday, anniversary) covers today + the next 7 days
   by OR-ing the month/day pairs, so the Today and Upcoming chips + dialog
   tabs all work from just 2 requests.

   ⚠ CONFIRM — CUSTOMER column names other than DOB / ANNVERSARY are
   guessed. Each field below lists candidates; the first one that exists
   in the row is used. If nothing matches, a console warning prints the
   real column names so you can add the right one.
   ════════════════════════════════════════════════════════════════ */
const CUSTOMER_SOURCE = {
  table: "CUSTOMER",
  dobCol: "DOB",
  annCol: "ANNVERSARY", // spelled this way in your URL
  nameKeys: ["CUSTNAME", "CUSTOMERNAME", "CNAME", "NAME", "Dealername"],
  mobileKeys: ["MOBILE", "MOBILENO", "PHONENO", "PHONE", "MOBILENUM", "Mobilenum"],
  cityKeys: ["CITY", "CITYNAME", "AREA", "ADD2", "CityName"],
};

const pick = (row, keys) => {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && row[k] !== "") return row[k];
  }
  return "";
};

const toMonthDay = (dateStr) => {
  if (!dateStr) return null;
  const d = dayjs(dateStr);
  if (!d.isValid()) return null;
  return { month: d.month(), day: d.date(), year: d.year() };
};

const matchesDate = (parsed, target) =>
  !!parsed && parsed.month === target.month() && parsed.day === target.date();

// today + next 7 days — one fetch covers all of them
const UPCOMING_DATES = Array.from({ length: 7 }, (_, i) =>
  dayjs().add(i + 1, "day"),
);
const FETCH_DATES = [dayjs(), ...UPCOMING_DATES];

const buildMonthDayWhere = (col, dates) =>
  `${col} IS NOT NULL AND (` +
  dates
    .map((d) => `(MONTH(${col})=${d.month() + 1} AND DAY(${col})=${d.date()})`)
    .join(" OR ") +
  `)`;

/* Each returned object carries ONLY the date it was fetched for
   (_dob or _ann), so a customer matching both is listed once per kind
   and never double counted. Field names are normalised to what the
   ReminderRow UI already expects: Dealername / Mobilenum / CityName. */
const normalizeCustomer = (row, kind, src = CUSTOMER_SOURCE) => ({
  ...row,
  Dealername: pick(row, src.nameKeys),
  Mobilenum: pick(row, src.mobileKeys),
  CityName: pick(row, src.cityKeys),
  _dob: kind === "dob" ? toMonthDay(row[src.dobCol]) : null,
  _ann: kind === "ann" ? toMonthDay(row[src.annCol]) : null,
});

const useCustomerReminders = (tenantName) => {
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const src = CUSTOMER_SOURCE;
    const headers = tenantName ? { tenantName } : {};

    const fetchKind = async (col, kind) => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/ERP/GetDataFromGivenTableNameWithWhere`,
          {
            params: {
              tableName: src.table,
              where: buildMonthDayWhere(col, FETCH_DATES),
            },
            headers,
          },
        );
        const rows = Array.isArray(res.data) ? res.data : [];

        if (rows.length && !pick(rows[0], src.nameKeys)) {
          console.warn(
            `Greetings → ${src.table}: no customer-name column found. ` +
            `Columns returned: ${Object.keys(rows[0]).join(", ")}`,
          );
        }
        return rows.map((r) => normalizeCustomer(r, kind, src));
      } catch (e) {
        console.error(`Error fetching ${kind} reminders:`, e);
        return [];
      }
    };

    const load = async () => {
      const [dobList, annList] = await Promise.all([
        fetchKind(src.dobCol, "dob"),
        fetchKind(src.annCol, "ann"),
      ]);
      setCustomers([...dobList, ...annList]);
    };

    load();
  }, [tenantName]);

  return customers;
};

/* ── daily-rotating background theme ── */
const DAILY_THEMES = [
  {
    name: "Violet Dusk",
    gradient: "linear-gradient(150deg,#2d1b69 0%,#5b3fa0 55%,#a85fa8 100%)",
    shadow: "rgba(44,27,105,0.35)",
  },
  {
    name: "Amber Ember",
    gradient: "linear-gradient(150deg,#7c2d12 0%,#c2410c 55%,#f59e0b 100%)",
    shadow: "rgba(124,45,18,0.35)",
  },
  {
    name: "Midnight Sky",
    gradient: "linear-gradient(150deg,#0f172a 0%,#1e3a8a 55%,#0ea5e9 100%)",
    shadow: "rgba(15,23,42,0.4)",
  },
  {
    name: "Emerald Tide",
    gradient: "linear-gradient(150deg,#134e4a 0%,#0f766e 55%,#2dd4bf 100%)",
    shadow: "rgba(19,78,74,0.35)",
  },
  {
    name: "Crimson Rose",
    gradient: "linear-gradient(150deg,#3f0d12 0%,#9d174d 55%,#f43f5e 100%)",
    shadow: "rgba(63,13,18,0.35)",
  },
  {
    name: "Indigo Bloom",
    gradient: "linear-gradient(150deg,#1e1b4b 0%,#4338ca 55%,#818cf8 100%)",
    shadow: "rgba(30,27,75,0.4)",
  },
  {
    name: "Forest Glow",
    gradient: "linear-gradient(150deg,#052e16 0%,#15803d 55%,#4ade80 100%)",
    shadow: "rgba(5,46,22,0.35)",
  },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const getDailyTheme = () => {
  const dayIndex = Math.floor(Date.now() / MS_PER_DAY);
  return DAILY_THEMES[dayIndex % DAILY_THEMES.length];
};

/* ── animated number counter ── */
const AnimatedAmount = ({ value }) => (
  <motion.span
    key={value}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
  >
    ₹{Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
  </motion.span>
);

/* ── single due row in popover ── */
const DueRow = ({ name, area, amount, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.05, duration: 0.28 }}
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "5px 0",
      borderBottom: "1px solid #f0eaff",
    }}
  >
    <span style={{ flex: 1, marginRight: 12, minWidth: 0 }}>
      <span
        style={{
          display: "block",
          fontSize: 12,
          color: "#4c3494",
          fontWeight: 600,
        }}
      >
        {name}
      </span>
      {area && area !== "-" && (
        <span style={{ display: "block", fontSize: 9, color: "#aaa" }}>
          {area}
        </span>
      )}
    </span>
    <span
      style={{
        fontSize: 12,
        fontWeight: 800,
        color: "#e05252",
        whiteSpace: "nowrap",
      }}
    >
      ₹
      {Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}
    </span>
  </motion.div>
);

/* ── Today's Dues card — bright white card floating on the dark panel ── */
const DueSection = ({
  label,
  icon,
  amount,
  accent,
  popoverContent,
  popoverTitle,
  onCheck,
  isPopover,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    style={{
      background: "#fff",
      borderRadius: 12,
      padding: "10px 12px",
      boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: -22,
        right: -22,
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: `${accent}30`,
        pointerEvents: "none",
      }}
    />

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            background: `${accent}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            color: accent,
          }}
        >
          {icon}
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "#2d1b69",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </span>
      </div>
    </div>

    <div
      style={{
        fontSize: 17,
        fontWeight: 800,
        color: "#2d1b69",
        letterSpacing: -0.3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        width: "100%",
      }}
    >
      <AnimatedAmount value={amount} />
      {isPopover ? (
        <Popover
          content={popoverContent}
          title={
            <span style={{ fontSize: 12, fontWeight: 700, color: "#2d1b69" }}>
              {popoverTitle}
            </span>
          }
          trigger="click"
          overlayStyle={{ maxWidth: 280 }}
          overlayInnerStyle={{ borderRadius: 12, padding: "10px 12px" }}
        >
          <motion.button
            whileHover={{ scale: 1.06, backgroundColor: accent, color: "#fff" }}
            whileTap={{ scale: 0.94 }}
            style={{
              border: `1.5px solid ${accent}`,
              background: "transparent",
              color: accent,
              borderRadius: 20,
              padding: "2px 10px",
              fontSize: 10,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 3,
              transition: "background 0.2s, color 0.2s",
            }}
          >
            View <ArrowRightOutlined style={{ fontSize: 9 }} />
          </motion.button>
        </Popover>
      ) : (
        <motion.button
          whileHover={{ scale: 1.06, backgroundColor: accent, color: "#fff" }}
          whileTap={{ scale: 0.94 }}
          onClick={onCheck}
          style={{
            border: `1.5px solid ${accent}`,
            background: "transparent",
            color: accent,
            borderRadius: 20,
            padding: "2px 10px",
            fontSize: 10,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 3,
            transition: "background 0.2s, color 0.2s",
          }}
        >
          View <ArrowRightOutlined style={{ fontSize: 9 }} />
        </motion.button>
      )}
    </div>
  </motion.div>
);

/* ==========================================================================
 * Birthday / Anniversary reminders — UI
 * ========================================================================*/

const ReminderRow = ({ cust, kind, index }) => {
  const emoji = kind === "birthday" ? "🎂" : "💍";
  const accent = kind === "birthday" ? "#e05780" : "#4c3494";
  const parsed = kind === "birthday" ? cust._dob : cust._ann;
  const yearLabel =
    parsed?.year && parsed.year > 1900
      ? kind === "birthday"
        ? `Turns ${dayjs().year() - parsed.year}`
        : `${dayjs().year() - parsed.year} yrs`
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.25 }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#fff",
        border: `1px solid ${accent}22`,
        borderRadius: 10,
        padding: "8px 10px",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flexShrink: 0,
          background: `${accent}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 15,
        }}
      >
        {emoji}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#2d1b69",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {cust.Dealername || "—"}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 2,
            fontSize: 10,
            color: "#999",
          }}
        >
          {cust.Mobilenum && cust.Mobilenum !== "-" && (
            <span>
              <PhoneOutlined style={{ marginRight: 3 }} />
              {cust.Mobilenum}
            </span>
          )}
          {cust.CityName && cust.CityName !== "-" && (
            <span>
              <EnvironmentOutlined style={{ marginRight: 3 }} />
              {cust.CityName}
            </span>
          )}
        </div>
      </div>
      {yearLabel && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: accent,
            background: `${accent}15`,
            padding: "3px 8px",
            borderRadius: 20,
            whiteSpace: "nowrap",
          }}
        >
          {yearLabel}
        </span>
      )}
    </motion.div>
  );
};

/* Section heading has a Send button on the right */
const ReminderSection = ({ heading, emoji, kind, list, date, onSend }) => {
  const disabled = list.length === 0;
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 800,
            color: "#4c3494",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          <span>{emoji}</span>
          {heading}
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#fff",
              background: "#b8a4e8",
              borderRadius: 20,
              padding: "1px 7px",
            }}
          >
            {list.length}
          </span>
        </div>

        <motion.button
          whileHover={disabled ? {} : { scale: 1.05 }}
          whileTap={disabled ? {} : { scale: 0.95 }}
          disabled={disabled}
          onClick={() => onSend && onSend(list, kind, date)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            border: `1px solid ${disabled ? "#e5deffb0" : "#7c5cbf"}`,
            background: disabled ? "#f5f0ff" : "#7c5cbf",
            color: disabled ? "#b8a4e8" : "#fff",
            borderRadius: 20,
            padding: "3px 10px",
            fontSize: 10,
            fontWeight: 700,
            cursor: disabled ? "not-allowed" : "pointer",
            flexShrink: 0,
          }}
        >
          <SendOutlined style={{ fontSize: 9 }} />
          Send
        </motion.button>
      </div>
      {list.length === 0 ? (
        <div style={{ padding: "10px 0" }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ fontSize: 11 }}>None on this date</span>}
          />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <AnimatePresence>
            {list.map((c, i) => (
              <ReminderRow
                key={`${kind}-${c.Mobilenum}-${c.Dealername}-${i}`}
                cust={c}
                kind={kind}
                index={i}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

const ReminderDialog = ({ open, onClose, title, dates, customers, onSend }) => {
  const [activeKey, setActiveKey] = useState(dates[0]?.format("YYYY-MM-DD"));

  useEffect(() => {
    if (open) setActiveKey(dates[0]?.format("YYYY-MM-DD"));
  }, [open, dates]);

  const items = dates.map((d) => {
    const key = d.format("YYYY-MM-DD");
    const isToday = d.isSame(dayjs(), "day");
    const birthdays = customers.filter((c) => matchesDate(c._dob, d));
    const anniversaries = customers.filter((c) => matchesDate(c._ann, d));

    return {
      key,
      label: (
        <div style={{ textAlign: "center", lineHeight: 1.15, padding: "0 2px" }}>
          <div style={{ fontSize: 11, fontWeight: 800 }}>
            {isToday ? "Today" : d.format("DD MMM")}
          </div>
          <div style={{ fontSize: 9, opacity: 0.6 }}>{d.format("ddd")}</div>
        </div>
      ),
      children: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 18,
            padding: "10px 2px 2px",
            maxHeight: 420,
            overflowY: "auto",
          }}
        >
          <ReminderSection
            heading="Birthdays"
            emoji="🎂"
            kind="birthday"
            list={birthdays}
            date={d}
            onSend={onSend}
          />
          <ReminderSection
            heading="Anniversaries"
            emoji="💍"
            kind="anniversary"
            list={anniversaries}
            date={d}
            onSend={onSend}
          />
        </div>
      ),
    };
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      destroyOnClose
      title={
        <span style={{ fontSize: 14, fontWeight: 800, color: "#2d1b69" }}>
          {title}
        </span>
      }
    >
      <style>{`
        .reminder-tabs .ant-tabs-nav::before { border-bottom: 1px solid #f5f0ff; }
        .reminder-tabs .ant-tabs-tab {
          background: #f5f0ff !important;
          border: 1px solid #ede5ff !important;
          border-radius: 10px !important;
          margin: 0 4px 6px 0 !important;
          padding: 5px 10px !important;
        }
        .reminder-tabs .ant-tabs-tab-active {
          background: linear-gradient(135deg,#2d1b69,#7c5cbf) !important;
          border-color: transparent !important;
        }
        .reminder-tabs .ant-tabs-tab-active * { color: #fff !important; }
        .reminder-tabs .ant-tabs-ink-bar { display: none; }
      `}</style>
      <Tabs
        className="reminder-tabs"
        activeKey={activeKey}
        onChange={setActiveKey}
        items={items}
        size="small"
      />
    </Modal>
  );
};

/* ── Today / Upcoming stat chip ── */
const ReminderStatSection = ({
  label,
  icon,
  birthdayCount,
  annCount,
  onClick,
  highlight,
  headerGradient,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    whileHover={{ y: -2 }}
    onClick={onClick}
    style={{
      cursor: "pointer",
      background: "#fff",
      borderRadius: 10,
      overflow: "hidden",
      boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
      flex: 1,
      minWidth: 0,
    }}
  >
    <div
      style={{
        background: headerGradient,
        padding: "4px 7px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
        <span style={{ fontSize: 10.5 }}>{icon}</span>
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 800,
            color: "#fff",
            textTransform: "uppercase",
            letterSpacing: 0.3,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        {highlight && (
          <motion.span
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#fff",
              flexShrink: 0,
            }}
          />
        )}
      </div>
      <ArrowRightOutlined style={{ fontSize: 8, color: "#fff", flexShrink: 0 }} />
    </div>

    <div style={{ display: "flex", gap: 4, padding: "5px 6px" }}>
      <div
        style={{
          flex: 1,
          background: "#ffe3ec",
          borderRadius: 7,
          padding: "3px 2px 4px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 12, lineHeight: 1 }}>🎂</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#e05780", lineHeight: 1.2 }}>
          {birthdayCount}
        </div>
        <div
          style={{
            fontSize: 6.5,
            fontWeight: 800,
            color: "#e05780",
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          Bday
        </div>
      </div>
      <div
        style={{
          flex: 1,
          background: "#ece3ff",
          borderRadius: 7,
          padding: "3px 2px 4px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 12, lineHeight: 1 }}>💍</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#4c3494", lineHeight: 1.2 }}>
          {annCount}
        </div>
        <div
          style={{
            fontSize: 6.5,
            fontWeight: 800,
            color: "#4c3494",
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          Anniv
        </div>
      </div>
    </div>
  </motion.div>
);

const LatestDues = () => {
  const [reminderDialog, setReminderDialog] = useState(null); // "today" | "upcoming" | null

  const tenantName = localStorage.getItem("tenantName");

  // Today's Dues — from TRANS_ENTRY_DATA_CUST
  const { list: todayDuesList, total: totalTodayDues } =
    useTodayDues(tenantName);

  // Birthdays & anniversaries — from CUSTOMER (see DATA LAYER above)
  const customers = useCustomerReminders(tenantName);

  const dailyTheme = useMemo(() => getDailyTheme(), []);

  const today = dayjs();

  const todayBirthdayCount = useMemo(
    () => customers.filter((c) => matchesDate(c._dob, today)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers],
  );
  const todayAnnCount = useMemo(
    () => customers.filter((c) => matchesDate(c._ann, today)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers],
  );
  const upcomingBirthdayCount = useMemo(
    () =>
      customers.filter((c) => UPCOMING_DATES.some((d) => matchesDate(c._dob, d)))
        .length,
    [customers],
  );
  const upcomingAnnCount = useMemo(
    () =>
      customers.filter((c) => UPCOMING_DATES.some((d) => matchesDate(c._ann, d)))
        .length,
    [customers],
  );

  const hasTodayCelebration = todayBirthdayCount + todayAnnCount > 0;

  /* ── Today's dues popover content ── */
  const todayPopoverContent = (
    <div style={{ maxHeight: 220, overflowY: "auto", minWidth: 220 }}>
      {todayDuesList.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: "#b8a4e8",
            padding: "12px 0",
            fontSize: 12,
          }}
        >
          No dues today 🎉
        </div>
      ) : (
        todayDuesList.map((due, i) => (
          <DueRow
            key={`${due.CustName}-${due.Area || ""}-${i}`}
            index={i}
            name={due.CustName}
            area={due.Area}
            amount={due.BalanceAmt}
          />
        ))
      )}
    </div>
  );

  /* TODO: wire this to your real SMS/WhatsApp/email sending API.
     `list` is the exact customers shown for that date + kind. */
  const handleSendGreeting = (list, kind, date) => {
    message.success(
      `Queued ${kind === "birthday" ? "birthday" : "anniversary"} greetings for ${list.length} customer${list.length !== 1 ? "s" : ""
      } (${date.format("DD MMM")})`,
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      style={{
        borderRadius: 14,
        background: dailyTheme.gradient,
        boxShadow: `0 6px 22px ${dailyTheme.shadow}`,
        padding: "12px 12px 10px",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 7,
            background: "rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GiftOutlined style={{ fontSize: 12, color: "#fff" }} />
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: "#fff", letterSpacing: 0.2 }}>
          Greetings
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <ReminderStatSection
            label="Today"
            icon="🎉"
            birthdayCount={todayBirthdayCount}
            annCount={todayAnnCount}
            onClick={() => setReminderDialog("today")}
            highlight={hasTodayCelebration}
            headerGradient="linear-gradient(135deg,#ff8a65,#e05780)"
          />
          <ReminderStatSection
            label="Upcoming"
            icon="📅"
            birthdayCount={upcomingBirthdayCount}
            annCount={upcomingAnnCount}
            onClick={() => setReminderDialog("upcoming")}
            highlight={false}
            headerGradient="linear-gradient(135deg,#5b3fa0,#7c5cbf)"
          />
        </div>

        <DueSection
          label="Today's Dues"
          icon={<MoneyCollectOutlined />}
          amount={totalTodayDues}
          accent="#7c5cbf"
          popoverContent={todayPopoverContent}
          popoverTitle="Today's Dues"
          isPopover
        />
      </div>

      <ReminderDialog
        open={reminderDialog === "today"}
        onClose={() => setReminderDialog(null)}
        title="Today's Birthdays & Anniversaries"
        dates={[today]}
        customers={customers}
        onSend={handleSendGreeting}
      />
      <ReminderDialog
        open={reminderDialog === "upcoming"}
        onClose={() => setReminderDialog(null)}
        title="Upcoming — Next 7 Days"
        dates={UPCOMING_DATES}
        customers={customers}
        onSend={handleSendGreeting}
      />
    </motion.div>
  );
};

export default LatestDues;